import React, { useRef } from "react";
import "bootstrap/dist/css/bootstrap.min.css";
import { Table } from "react-bootstrap";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "jspdf-autotable";

const utahData = "/img/headerlogo.png";
const bodex = "/img/bodexLogo.png";
const kpi = "/img/KPI-DASH 2.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const TableNo37 = () => {
  const pdfRef = useRef();

  // 📊 Employment Data
  const industryData = [
    { sector: "Aerospace Product and Parts Manufacturing", employment: 14059 },
    {
      sector: "Medical Equipment and Supplies Manufacturing",
      employment: 12374,
    },
    {
      sector:
        "Navigational, Measuring, Electromedical, and Control Instruments Manufacturing",
      employment: 8575,
    },
    { sector: "Pharmaceutical and Medicine Manufacturing", employment: 8019 },
    { sector: "Other Miscellaneous Manufacturing", employment: 7636 },
    {
      sector: "Architectural and Structural Metals Manufacturing",
      employment: 7224,
    },
    { sector: "Plastics Product Manufacturing", employment: 6465 },
    { sector: "Printing and Related Support Activities", employment: 5202 },
    { sector: "Animal Slaughtering and Processing", employment: 4825 },
    { sector: "Bakeries and Tortilla Manufacturing", employment: 4381 },
    { sector: "Dairy Product Manufacturing", employment: 4084 },
    { sector: "Furniture and Kitchen Cabinet Manufacturing", employment: 3940 },
    { sector: "Cement and Concrete Product Manufacturing", employment: 3089 },
    {
      sector: "Semiconductor and Electronic Component Manufacturing",
      employment: 2895,
    },
    { sector: "Machine Shops and Metal Fasteners", employment: 2757 },
    { sector: "Other Food Manufacturing", employment: 2711 },
    { sector: "General Purpose Machinery Manufacturing", employment: 2523 },
    {
      sector: "Other Fabricated Metal Product Manufacturing",
      employment: 2380,
    },
  ];

  // 🧮 Insights
  const totalEmployment = industryData.reduce((a, b) => a + b.employment, 0);
  const avgEmployment = Math.round(totalEmployment / industryData.length);
  const topIndustry = industryData[0];
  const lowestIndustry = industryData[industryData.length - 1];

  // 📈 Chart
  const chartData = {
    labels: industryData.map((i) => i.sector),
    datasets: [
      {
        label: "Employment Count (2024)",
        data: industryData.map((i) => i.employment),
        backgroundColor: "#B5EAD6",
        borderColor: "#B5EAD6",
        borderWidth: 1,
        borderRadius: 6,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        title: {
          display: true,
          text: "Employment Count",
          color: "#111",
          font: { size: 14, weight: "bold" },
        },
        ticks: {
          color: "#333",
          callback: (val) => val.toLocaleString(),
        },
      },
      x: {
        ticks: {
          color: "#333",
          autoSkip: false,
          maxRotation: 75,
          minRotation: 45,
          font: { size: 10 },
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: { font: { size: 12, weight: "bold" } },
      },
      tooltip: {
        callbacks: {
          label: (ctx) => `${ctx.dataset.label}: ${ctx.raw.toLocaleString()}`,
        },
      },
    },
  };

  // 📄 PDF Export
  const downloadPDF = () => {
    const input = pdfRef.current;

    html2canvas(input, {
      scale: 2, // higher for sharper text
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      // A4 size in mm
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();

      // Convert canvas height to mm
      const imgWidth = pageWidth - 10; // leave small margins
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      // Header
      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      // Footer
      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      // First page
      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      // More pages if needed
      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      // Footer for the last page
      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("utahdata_industry_employment_2024.pdf");
      // input.style.width = originalWidth;
    });
  };

  // 🧩 UI
  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-yellow-50 via-white to-pink-50 rounded-xl shadow-lg"
      >
        {/* Header */}
        <h2 className="text-2xl font-bold mb-4 text-center text-gray-800">
          Employment by Industry Sector (2024)
        </h2>
        <p className="text-center text-gray-600 mb-6">
          Breakdown of Utah’s 2024 employment across major manufacturing
          sectors. Aerospace and medical equipment industries lead the workforce
          growth.
        </p>

        {/* Insight Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-red-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Total Employment
            </p>
            <p className="text-lg font-bold text-red-700">
              {totalEmployment.toLocaleString()}
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">Top Sector</p>
            <p className="text-lg font-bold text-green-700">
              {topIndustry.sector}
            </p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Average Employment
            </p>
            <p className="text-lg font-bold text-yellow-700">
              {avgEmployment.toLocaleString()}
            </p>
          </div>
        </div>

        {/* Table + Chart Layout */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-[#B5EAD6] text-black">
                <tr>
                  <th className="p-2">Industry Sector</th>
                  <th className="p-2">Employment Count</th>
                </tr>
              </thead>
              <tbody>
                {industryData.map((row, i) => (
                  <tr
                    key={i}
                    className="hover:bg-gray-100 transition bg-white even:bg-gray-50"
                  >
                    <td className="p-2 text-left">{row.sector}</td>
                    <td className="p-2 font-medium">
                      {row.employment.toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Chart */}
          <div className="bg-white rounded-lg shadow-md p-4">
            <div style={{ height: "400px", width: "100%" }}>
              <Bar data={chartData} options={options} />
            </div>
          </div>
        </div>

        {/* Summary */}
        <p className="text-center text-gray-600">
          📊 Utah’s manufacturing sector employs{" "}
          <span className="font-semibold text-red-700">
            {totalEmployment.toLocaleString()}
          </span>{" "}
          workers in 2024. The{" "}
          <span className="font-semibold text-green-700">
            {topIndustry.sector}
          </span>{" "}
          tops the list, while{" "}
          <span className="font-semibold text-yellow-700">
            {lowestIndustry.sector}
          </span>{" "}
          represents smaller specialized segments of production.
        </p>
      </div>

      {/* Download Button */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
};

export default TableNo37;
